import {
  Box,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import {
  PlanId,
  usePlanId,
  useSetPlanId,
} from "components/common/boundary/PlanId";
import { PlanName, useDefaultPlanName } from "components/plan/name";
import { ApprovePlan } from "components/plan/topButtons/editAndSendPlan/ApprovePlan";
import { Download } from "components/plan/topButtons/editAndSendPlan/Download";
import { SendToScrapyard } from "components/plan/topButtons/editAndSendPlan/SendToScrapyard";
import { usePlan } from "contexts/plan";
import { useTenantTranslation } from "hooks/formatters";
import { useIsMobile } from "hooks/useIsMobile";
import React from "react";

export enum State {
  ApprovePlan,
  SendToScrapyard,
  Download,
}

const toNextState = (state: State): State.SendToScrapyard | State.Download => {
  switch (state) {
    case State.ApprovePlan:
      return State.SendToScrapyard;
    case State.SendToScrapyard:
    case State.Download:
      return State.Download;
  }
};

const toPreviousState = (
  state: State
): State.ApprovePlan | State.SendToScrapyard => {
  switch (state) {
    case State.ApprovePlan:
    case State.SendToScrapyard:
      return State.ApprovePlan;
    case State.Download:
      return State.SendToScrapyard;
  }
};

const stateToIndex = (state: State): 0 | 1 | 2 => {
  switch (state) {
    case State.ApprovePlan:
      return 0;
    case State.SendToScrapyard:
      return 1;
    case State.Download:
      return 2;
  }
};

export const EditAndSendPlanPage = () => {
  const [state, setState] = React.useState(State.ApprovePlan);
  const planId = usePlanId();
  const { plan } = usePlan(planId);
  const defaultName = useDefaultPlanName()(
    plan.status === "success" ? plan.data.plan_index : 0
  );

  const setPlanId = useSetPlanId();
  const { t } = useTenantTranslation();
  const isMobile = useIsMobile();

  const steps = isMobile
    ? [t("approve"), t("send"), t("download")]
    : [t("approvePlan"), t("sendToScrapyard"), t("download")];

  const handleToNextState = React.useCallback(() => {
    setState(toNextState);
  }, [setState]);

  const handleToPreviousState = React.useCallback(() => {
    setState(toPreviousState);
  }, [setState]);

  const toInitialState = React.useCallback(() => {
    setState(State.ApprovePlan);
  }, [setState]);

  const handleSetPlanId = React.useCallback(
    (id: PlanId) => {
      setPlanId(id, true);
    },
    [setPlanId]
  );

  const body = React.useMemo((): React.JSX.Element => {
    switch (state) {
      case State.ApprovePlan:
        return (
          <ApprovePlan
            toNextState={handleToNextState}
            planId={planId}
            setPlanId={handleSetPlanId}
          />
        );
      case State.Download:
        return (
          <Download
            planId={planId}
            doClose={() => {
              window.close();
            }}
          />
        );
      case State.SendToScrapyard:
        return (
          <SendToScrapyard
            planId={planId}
            toNextState={handleToNextState}
            toPreviousState={handleToPreviousState}
            toInitialState={toInitialState}
          />
        );
    }
  }, [
    state,
    handleToNextState,
    handleToPreviousState,
    planId,
    handleSetPlanId,
    toInitialState,
  ]);

  return (
    <Stack sx={{ height: "100%" }}>
      <Box sx={{ p: 2 }}>
        <PlanName
          planName={defaultName}
          defaultName={defaultName}
          variant={isMobile ? "h4" : "h2"}
        />
      </Box>
      <Stepper
        activeStep={stateToIndex(state)}
        alternativeLabel={isMobile}
        sx={{
          mb: 2,
          mx: { xs: 1, sm: 2 },
          flexShrink: 0,
          pt: 2,
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>
              <Typography variant={isMobile ? "body2" : "body1"}>
                {label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <Stack
        sx={{
          mx: { xs: 1, sm: 2 },
          mb: 2,
          flex: 1,
          overflow: "auto",
        }}
      >
        {body}
      </Stack>
    </Stack>
  );
};
